import { gql } from "@apollo/client";
import { IAutopilotContactDetailsFilters } from "./useAutopilotUrlQueryParams";
import { useEffect, useMemo, useState } from "react";

export const FilterAutopilotContacts_ContactList = gql`
  fragment FilterAutopilotContacts_ContactList on ContactList {
    contacts {
      id
      created_at
      org_contact {
        id
        first_name
        last_name
        company_name
        orgEmails {
          email
        }
        orgPhoneNumbers {
          phone_number
        }
      }
    }
  }
`;

export const FilterAutopilotContacts_AutopilotSession = gql`
  fragment FilterAutopilotContacts_AutopilotSession on AutopilotSession {
    contacts {
      id
      created_at
      org_contact {
        id
        first_name
        last_name
        company_name
        orgEmails {
          email
        }
        orgPhoneNumbers {
          phone_number
        }
      }
      scoringCriteriaAnalysis {
        matched_criteria
        scoring_criterion_id
      }
    }
  }
`;

type AutopilotContact = {
  id: number;
  created_at: any;
  org_contact: {
    id?: number | null | undefined;
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    orgEmails: { email: string }[];
    orgPhoneNumbers: { phone_number: string }[];
  };
  scoringCriteriaAnalysis?: {
    matched_criteria?: boolean | null;
    scoring_criterion_id?: number | null;
  }[];
};

const useFilterAndSortAutopilotContacts = ({
  contacts,
  filters,
  scoringCriteriaOrder,
}: {
  contacts: AutopilotContact[] | null | undefined;
  filters: IAutopilotContactDetailsFilters;
  scoringCriteriaOrder: number[] | null | undefined;
}) => {
  const [
    filteredContactIdsToOrgContactIds,
    setFilteredContactIdsToOrgContactIds,
  ] = useState<Map<number, number>>(new Map());

  const contactIdToOrgContactId = useMemo(() => {
    return new Map(
      contacts?.map((contact) => [contact.id, contact.org_contact.id!]) ?? [],
    );
  }, [contacts]);

  useEffect(() => {
    const newFilteredContactIds = (contacts ?? [])
      .filter((contact) => {
        if (filters.companyNameFilter.length > 0) {
          if (
            !filters.companyNameFilter.some((filterName) =>
              contact.org_contact.company_name
                ?.toLowerCase()
                .startsWith(filterName.toLowerCase()),
            )
          ) {
            return false;
          }
        }
        if (filters.excludedCompanyNameFilter?.length > 0) {
          if (
            filters.excludedCompanyNameFilter.some((filterName) =>
              contact.org_contact.company_name
                ?.toLowerCase()
                .startsWith(filterName.toLowerCase()),
            )
          ) {
            return false;
          }
        }
        if (filters.firstNameFilter.length > 0) {
          const firstName = contact.org_contact.first_name || "";
          if (
            !filters.firstNameFilter.some((filterName) =>
              firstName.toLowerCase().startsWith(filterName.toLowerCase()),
            )
          ) {
            return false;
          }
        }
        if (filters.lastNameFilter.length > 0) {
          const lastName = contact.org_contact.last_name || "";
          if (
            !filters.lastNameFilter.some((filterName) =>
              lastName.toLowerCase().startsWith(filterName.toLowerCase()),
            )
          ) {
            return false;
          }
        }
        if (filters.hasEmailFilter !== null) {
          const hasEmail = (contact.org_contact.orgEmails?.length ?? 0) > 0;
          if (hasEmail !== filters.hasEmailFilter) {
            return false;
          }
        }
        if (filters.hasPhoneNumberFilter !== null) {
          const hasPhoneNumber =
            (contact.org_contact.orgPhoneNumbers?.length ?? 0) > 0;
          if (hasPhoneNumber !== filters.hasPhoneNumberFilter) {
            return false;
          }
        }
        if (filters.scoringCriteriaFilter.length > 0) {
          // Group filters by criterion ID
          const filtersByCriterionId = filters.scoringCriteriaFilter.reduce(
            (acc, filter) => {
              const [criterionId] = filter.split("_");
              if (!acc[criterionId]) {
                acc[criterionId] = [];
              }
              acc[criterionId].push(filter);
              return acc;
            },
            {} as Record<string, string[]>,
          );

          // Check each criterion group matches the expected state
          for (const [criterionId, criterionFilters] of Object.entries(
            filtersByCriterionId,
          )) {
            const analysis = contact.scoringCriteriaAnalysis?.find(
              (criteria) =>
                criteria.scoring_criterion_id === parseInt(criterionId),
            );

            const possibleMatchOptions = [] as (boolean | null | undefined)[];

            if (criterionFilters.some((f) => f.endsWith("_matches"))) {
              possibleMatchOptions.push(true);
            }

            if (criterionFilters.some((f) => f.endsWith("_does_not_match"))) {
              possibleMatchOptions.push(false);
            }

            if (criterionFilters.some((f) => f.endsWith("_unknown"))) {
              possibleMatchOptions.push(null);
              possibleMatchOptions.push(undefined);
            }

            if (
              possibleMatchOptions.length > 0 &&
              !possibleMatchOptions.includes(analysis?.matched_criteria)
            ) {
              return false;
            }
          }
        }
        return true;
      })
      .sort((a, b) => {
        const directionalMultiplier = filters.sortDirection === "desc" ? -1 : 1;
        if (filters.sortBy === "companyName") {
          if (!a.org_contact.company_name) return 1 * directionalMultiplier;
          if (!b.org_contact.company_name) return -1 * directionalMultiplier;
          return (
            a.org_contact.company_name.localeCompare(
              b.org_contact.company_name,
            ) * directionalMultiplier
          );
        } else if (filters.sortBy === "name") {
          if (!a.org_contact.first_name) return 1 * directionalMultiplier;
          if (!b.org_contact.first_name) return -1 * directionalMultiplier;
          const firstNameCompare = a.org_contact.first_name.localeCompare(
            b.org_contact.first_name,
          );
          if (firstNameCompare !== 0)
            return firstNameCompare * directionalMultiplier;

          // Compare last names if first names are equal
          if (!a.org_contact.last_name) return 1 * directionalMultiplier;
          if (!b.org_contact.last_name) return -1 * directionalMultiplier;
          return (
            a.org_contact.last_name.localeCompare(b.org_contact.last_name) *
            directionalMultiplier
          );
        } else if (filters.sortBy === "criteriaMatched") {
          // Sort by number of matched criteria
          const getMatchedCriteriaCount = (contact: any) => {
            return (
              contact.scoringCriteriaAnalysis?.filter(
                (criteria: any) => criteria.matched_criteria,
              ).length ?? 0
            );
          };
          const aMatchCount = getMatchedCriteriaCount(a);
          const bMatchCount = getMatchedCriteriaCount(b);

          return (bMatchCount - aMatchCount) * directionalMultiplier;
        } else if (filters.sortBy?.startsWith("scoringCriteria_")) {
          const criterionId = filters.sortBy.split("_")[1];
          const aMatch = a.scoringCriteriaAnalysis?.find(
            (criteria: any) =>
              criteria.scoring_criterion_id === parseInt(criterionId),
          )?.matched_criteria;
          const bMatch = b.scoringCriteriaAnalysis?.find(
            (criteria: any) =>
              criteria.scoring_criterion_id === parseInt(criterionId),
          )?.matched_criteria;
          if (aMatch !== bMatch) {
            return (
              ((bMatch ? 1 : 0) - (aMatch ? 1 : 0)) * directionalMultiplier
            );
          }
          return 0;
        } else {
          // DEFAULT SORT
          // Sort by number of matched criteria
          const getMatchedCriteriaCount = (contact: any) => {
            return (
              contact.scoringCriteriaAnalysis?.filter(
                (criteria: any) => criteria.matched_criteria,
              ).length ?? 0
            );
          };
          const aMatchCount = getMatchedCriteriaCount(a);
          const bMatchCount = getMatchedCriteriaCount(b);

          if (aMatchCount !== bMatchCount) {
            return (bMatchCount - aMatchCount) * directionalMultiplier;
          }

          let valueA: boolean | null = null;
          let valueB: boolean | null = null;
          let index = 0;

          while (
            valueA === valueB &&
            index < (scoringCriteriaOrder?.length ?? 0)
          ) {
            const currentCriterionId = scoringCriteriaOrder?.[index];
            valueA =
              a.scoringCriteriaAnalysis?.find(
                (r) => r.scoring_criterion_id === currentCriterionId,
              )?.matched_criteria ?? null;
            valueB =
              b.scoringCriteriaAnalysis?.find(
                (r) => r.scoring_criterion_id === currentCriterionId,
              )?.matched_criteria ?? null;
            index++;
          }

          return valueA ? -1 : 1;
        }
      })
      .map((contact) => contact.id!);
    setFilteredContactIdsToOrgContactIds(
      new Map(
        newFilteredContactIds.map((contactId) => [
          contactId,
          contactIdToOrgContactId.get(contactId)!,
        ]),
      ),
    );
  }, [contacts, filters, contactIdToOrgContactId, scoringCriteriaOrder]);

  return {
    filteredContactIdsToOrgContactIds,
  };
};

export default useFilterAndSortAutopilotContacts;
